import MeetingStatus from 'enums/MeetingStatus';
import VideoStatus from 'enums/VideoStatus';
import logger from 'logger';
import { select, takeEvery } from 'redux-saga/effects';
import * as chimeActions from 'store/chime/chimeActions';
import {
  addAudioObserversSaga,
  getAudioVideoDevicesSaga,
  getJoinInfoSaga,
} from 'store/chime/chimeSagas';
import { recordSaga } from 'store/classroom/classroomSagas';
import * as messageActions from 'store/message/messageActions';
import * as userActions from 'store/user/userActions';
import { call, put } from 'store/utils';
import * as videoActions from 'store/video/videoActions';
import { authConferenceUser, getAllMeetingMessages } from 'utils/apiCalls';
import * as classroomActions from './classroomActions';

export function* joinMeetingFlowSaga(action) {
  const key = 'joinMeeting';
  yield put(classroomActions.initAction({ key }));

  yield put(chimeActions.setMeetingStatusAction({ meetingStatus: MeetingStatus.Loading }));
  try {
    const { externalMeetingId, externalUserId, audioRef } = action.payload;
    yield put(chimeActions.setInitialParamsAction({ externalMeetingId, externalUserId }));
    yield call(getJoinInfoSaga);

    yield call(getAudioVideoDevicesSaga);

    yield call(addAudioObserversSaga, audioRef);
    yield put(chimeActions.setMeetingStatusAction({ meetingStatus: MeetingStatus.Succeeded }));
    const { meetingId } = yield select(({ chime }) => ({ meetingId: chime.meetingId }));
    const messages = yield call(getAllMeetingMessages, externalMeetingId, meetingId);
    yield put(messageActions.setAllMessagesAction({ messages }));
    yield put(chimeActions.startMessagingAction());

    yield put(classroomActions.successAction({ key }));
  } catch (error) {
    yield put(chimeActions.setMeetingStatusAction({ meetingStatus: MeetingStatus.Failed }));
    yield put(classroomActions.errorAction({ key, error }));
  }
}

export function* toggleRosterAttendeeMutedFlowSaga(action) {
  const key = 'toggleRosterAttendeeMuted';
  yield put(classroomActions.initAction({ key }));
  try {
    const { selectedRosterAttendeeId } = action.payload;
    const { roster, attendeeId, meetingId, name } = yield select(({ chime }) => ({
      roster: chime.roster,
      attendeeId: chime.attendeeId,
      meetingId: chime.meetingId,
      name: chime.name,
    }));
    let selectedRosterAttendee = roster[selectedRosterAttendeeId];
    if (selectedRosterAttendeeId === attendeeId) {
      yield toggleMuteSelfFlowSaga();
    } else if (!selectedRosterAttendee.muted) {
      yield put(
        messageActions.sendMessageAction({
          type: 'mute',
          payload: {
            attendeeId: selectedRosterAttendeeId,
            meetingId,
            name,
          },
        })
      );
    }
    yield put(classroomActions.successAction({ key }));
  } catch (error) {
    yield put(classroomActions.errorAction({ key, error }));
  }
}

export function* toggleMuteSelfFlowSaga(action) {
  const key = 'toggleMuteSelfFlowSaga';
  yield put(classroomActions.initAction({ key }));
  try {
    const forceMute = action?.payload?.forceMute || false;
    const { muted, audioVideo, attendeeId } = yield select(({ chime, video }) => ({
      muted: video.muted,
      attendeeId: chime.attendeeId,
      audioVideo: chime.audioVideo,
    }));
    if (forceMute || !muted) {
      audioVideo.realtimeMuteLocalAudio();
    } else {
      audioVideo.realtimeUnmuteLocalAudio();
    }
    let isLocalMuted = audioVideo.realtimeIsLocalAudioMuted();
    yield put(videoActions.setMutedAction(isLocalMuted));
    yield put(
      chimeActions.updateRosterAttributeAction({
        attendeeId,
        key: 'muted',
        value: isLocalMuted,
      })
    );
    yield put(classroomActions.successAction({ key }));
  } catch (error) {
    yield put(classroomActions.errorAction({ key, error }));
  }
}

export function* enterMeetingFlowSaga(action) {
  const key = 'enterMeeting';
  yield put(classroomActions.initAction({ key }));
  try {
    const { videoRef } = action.payload;
    const { startWithVideo, startWithAudio, audioVideo } = yield select(
      ({ audioVideo, chime }) => ({
        startWithAudio: audioVideo.startWithAudio,
        startWithVideo: audioVideo.startWithVideo,
        audioVideo: chime.audioVideo,
      })
    );
    yield audioVideo.stopVideoPreviewForVideoInput(videoRef);
    if (!startWithAudio) {
      audioVideo.realtimeMuteLocalAudio();
      yield put(videoActions.setMutedAction(audioVideo.realtimeIsLocalAudioMuted()));
    }

    yield put(videoActions.toggleVideoStatusAction(VideoStatus.Disabled));
    if (startWithVideo) {
      yield put(videoActions.toggleVideoAction());
    }

    audioVideo.start();
    yield put(chimeActions.setHasJoinedAction({ hasJoined: true }));
    yield put(classroomActions.successAction({ key }));
  } catch (error) {
    yield put(classroomActions.errorAction({ key, error }));
  }
}

export function* recordMeetingFlowSaga(action) {
  const key = 'recordChimeMeeting';
  yield put(classroomActions.initAction({ key }));
  try {
    const { audioElement, token } = action.payload;
    localStorage.setItem('conference-token', btoa(token));
    const { newToken, tokenData } = yield call(authConferenceUser, token);
    localStorage.clear();
    localStorage.setItem('conference-token', btoa(newToken));

    yield put(
      userActions.setUserAction({
        userId: tokenData.UserId,
        admin: true,
        isRecordingUser: true,
      })
    );

    yield call(joinMeetingFlowSaga, {
      payload: {
        audioRef: audioElement.current,
        externalMeetingId: tokenData.sessionId,
        externalUserId: tokenData.userId,
      },
    });
    yield call(recordSaga);
    try {
      let { socket } = yield select(({ message }) => ({
        socket: message.socket,
      }));
      if (socket) {
        socket.close();
      }
    } catch (e) {
      logger.error(e);
    }

    yield put(classroomActions.successAction({ key }));
  } catch (error) {
    yield put(classroomActions.errorAction({ key, error }));
  }
}

export default function* classroomWatcher() {
  yield takeEvery(classroomActions.joinMeetingAction().type, joinMeetingFlowSaga);
  yield takeEvery(classroomActions.enterChimeMeetingAction().type, enterMeetingFlowSaga);
  yield takeEvery(classroomActions.recordChimeMeetingAction().type, recordMeetingFlowSaga);
  yield takeEvery(classroomActions.toggleMuteSelfAction().type, toggleMuteSelfFlowSaga);
  yield takeEvery(
    classroomActions.toggleRosterAttendeeMutedAction().type,
    toggleRosterAttendeeMutedFlowSaga
  );
}
